/**
 * Single global WebSocket connection. Journal events from the controller are
 * broadcast here; the bridge fans them into TanStack Query cache writes so
 * any open page reacts in real time without per-page subscriptions.
 *
 * Speed wins:
 *   - agent_reply_delta events stream into the messages cache as they arrive,
 *     so the user sees the model "type" the reply token-by-token.
 *   - reset_chat / agent_reply land directly in the cache too (no refetch
 *     round-trip), so the UI updates within ~10ms of the journal write.
 *   - Other events fall back to invalidate + refetch.
 */

import { useEffect, useSyncExternalStore } from 'react'
import { useQueryClient, type QueryClient } from '@tanstack/react-query'
import { WebSocket as ReconnectingWS } from 'partysocket'
import {
  applyReadReceiptToClient,
  messagesKey,
  preloadMessageReadModel,
  refreshMessageReadModel,
  refreshRetainedMessageReadModels,
  type Chat,
  type Message,
  type Topic,
} from './api'
import { clearReadNotificationsForChat } from './notificationClear'
import { signalOutboxDeliveryOpportunity } from './outbox'
import { prepareUpdate } from './sw'
import { changesEngagedTopicState } from './engagedTopicFilters'

type JournalEvent = {
  type?: string
  id?: number
  kind?: string
  chat_id?: string | null
  topic_id?: string | null
  lane_id?: string | null
  message_id?: string | null
  actor?: string
  payload?: Record<string, unknown>
  ts?: number
}

type JournalReplayResponse = {
  events?: JournalEvent[]
  truncated?: boolean
  latest_id?: number
}

let _ws: ReconnectingWS | null = null
const LAST_EVENT_ID_KEY = 'agentchat:last-journal-event-id'

// Live-stream gate. False until the WS opens for the first time this
// session; flips back to false on close. Consumed by lane-status rendering
// so cached IDB rows don't pessimistically show "Reconnecting" on every
// cold start before /api/chats has confirmed the heartbeats are fresh.
let _wsConnected = false
const _wsListeners = new Set<() => void>()

function setWsConnected(next: boolean): void {
  if (_wsConnected === next) return
  _wsConnected = next
  _wsListeners.forEach((fn) => fn())
}

function subscribeWsConnected(fn: () => void): () => void {
  _wsListeners.add(fn)
  return () => _wsListeners.delete(fn)
}

function getWsConnectedSnapshot(): boolean {
  return _wsConnected
}

export function useWsConnected(): boolean {
  return useSyncExternalStore(subscribeWsConnected, getWsConnectedSnapshot, getWsConnectedSnapshot)
}

let lastSeenEventId = readLastEventId()

function readLastEventId(): number {
  try {
    const raw = window.localStorage.getItem(LAST_EVENT_ID_KEY)
    const n = Number(raw)
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0
  } catch {
    return 0
  }
}

function rememberEventId(id: number): void {
  if (!Number.isFinite(id) || id <= lastSeenEventId) return
  lastSeenEventId = Math.floor(id)
  try {
    window.localStorage.setItem(LAST_EVENT_ID_KEY, String(lastSeenEventId))
  } catch {
    // Losing replay position is recoverable: chat/message REST queries are
    // still the source of truth on mount and foreground refetch.
  }
}

function invalidateForJournalCatchup(qc: QueryClient, parsed: JournalEvent): void {
  if (parsed.type === 'ping' || parsed.type === 'hello') return
  if (parsed.type === 'replay_truncated' || changesEngagedTopicState(parsed.kind)) {
    qc.invalidateQueries({ queryKey: ['engaged-topics'] })
  }
  if (parsed.type === 'replay_truncated') {
    qc.invalidateQueries({ queryKey: ['chats'] })
    qc.invalidateQueries({ queryKey: ['lanes'] })
    refreshRetainedMessageReadModels(qc)
    return
  }

  const k = parsed.kind
  const chatId = parsed.chat_id || undefined
  const payload = parsed.payload || {}
  const topicId = parsed.topic_id || (payload.topic_id as string | undefined) || undefined

  if (k === 'pwa_updated') {
    prepareUpdate(String(payload.cache || parsed.id || 'latest'))
    return
  }

  if (chatId) {
    qc.invalidateQueries({ queryKey: ['chat', chatId], exact: true })
    if (
      k === 'agent_reply' ||
      k === 'agent_reply_delta' ||
      k === 'enqueued' ||
      k === 'force_now' ||
      k === 'dispatched' ||
      k === 'idle' ||
      k === 'replied' ||
      k === 'dispatch_error'
    ) {
      preloadMessageReadModel(qc, chatId, topicId ?? null)
    } else if (k === 'reset_chat' || k === 'message_updated') {
      refreshMessageReadModel(qc, chatId, topicId ?? null)
    }
  }

  if (
    k === 'agent_reply' ||
    k === 'agent_reply_delta' ||
    k === 'reset_chat' ||
    k === 'enqueued' ||
    k === 'force_now' ||
    k === 'dispatched' ||
    k === 'idle' ||
    k === 'busy' ||
    k === 'replied' ||
    k === 'lane_missing' ||
    k === 'dispatch_error' ||
    k === 'watchdog_stale_lane' ||
    k === 'usage_limited' ||
    k === 'usage_recovered' ||
    k === 'turn_active' ||
    k === 'turn_stopped' ||
    k === 'turn_ended'
  ) {
    qc.invalidateQueries({ queryKey: ['chats'] })
    qc.invalidateQueries({ queryKey: ['lanes'] })
    if (chatId) qc.invalidateQueries({ queryKey: ['topics', chatId], exact: true })
    return
  }

  if (
    k === 'topic_spawned' ||
    k === 'topic_closed' ||
    k === 'topic_reopened' ||
    k === 'topic_renamed' ||
    k === 'topic_model_changed' ||
    k === 'topic_created' ||
    k === 'chat_read' ||
    k === 'snooze' ||
    k === 'unsnooze' ||
    k === 'pin' ||
    k === 'unpin'
  ) {
    qc.invalidateQueries({ queryKey: ['chats'] })
    if (chatId) qc.invalidateQueries({ queryKey: ['topics', chatId], exact: true })
    return
  }

  if (k === 'chat_model_changed' || k === 'chat_snoozed' || k === 'chat_unsnoozed') {
    qc.invalidateQueries({ queryKey: ['chats'] })
    if (chatId) qc.invalidateQueries({ queryKey: ['chat', chatId], exact: true })
  }
}

let _journalSyncPromise: Promise<boolean> | null = null
let _lastJournalSyncAt = 0

export async function syncJournalDeltas(qc: QueryClient): Promise<boolean> {
  if (typeof window === 'undefined') return false
  if (typeof navigator !== 'undefined' && !navigator.onLine) return true
  // Shared storage is only a bootstrap hint. Another installed window may
  // have processed events that this window's message cache has never seen.
  const now = Date.now()
  if (_journalSyncPromise) return _journalSyncPromise
  if (now - _lastJournalSyncAt < 750) return true
  _lastJournalSyncAt = now
  refreshRetainedMessageReadModels(qc)
  const sinceId = lastSeenEventId
  if (sinceId <= 0) return false

  _journalSyncPromise = (async () => {
    try {
      const response = await fetch(`/api/journal?since_id=${sinceId}&limit=1000`, {
        credentials: 'include',
      })
      if (!response.ok) return false
      const body = await response.json() as JournalReplayResponse
      const events = Array.isArray(body.events) ? body.events : []
      let changed = false

      for (const event of events) {
        const eventId = Number(event.id ?? 0)
        if (eventId > 0) {
          if (eventId <= lastSeenEventId) continue
          rememberEventId(eventId)
        }
        invalidateForJournalCatchup(qc, event)
        changed = true
      }

      if (body.truncated) {
        qc.invalidateQueries({ queryKey: ['chats'] })
        qc.invalidateQueries({ queryKey: ['lanes'] })
        refreshRetainedMessageReadModels(qc)
        changed = true
      }

      if (changed) {
        await Promise.allSettled([
          qc.refetchQueries({ queryKey: ['chats'], type: 'active' }),
          qc.refetchQueries({ queryKey: ['lanes'], type: 'active' }),
          qc.refetchQueries({ queryKey: ['work-monitor'], type: 'active' }),
        ])
      }
      return true
    } catch {
      return false
    } finally {
      _journalSyncPromise = null
    }
  })()
  return _journalSyncPromise
}

// Debounced topics-key invalidation. A bulk import or burst of topic state
// changes shouldn't spam React with re-renders.
const _topicsInvalidationTimers = new Map<string, number>()
function scheduleTopicsInvalidate(
  qc: ReturnType<typeof useQueryClient>,
  chatId: string,
  delayMs = 200,
): void {
  const existing = _topicsInvalidationTimers.get(chatId)
  if (existing !== undefined) window.clearTimeout(existing)
  const id = window.setTimeout(() => {
    _topicsInvalidationTimers.delete(chatId)
    qc.invalidateQueries({ queryKey: ['topics', chatId] })
  }, delayMs)
  _topicsInvalidationTimers.set(chatId, id)
}

// Server pings every 25s; we treat 35s without ANY traffic as a dead
// link and force a reconnect. The grace window absorbs schedule jitter
// and a one-event delay through ws_hub's queue.
const WATCHDOG_TIMEOUT_MS = 35_000
let _watchdogTimer: number | null = null

function buildWsUrl(): string {
  const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  const url = new URL(`${wsProtocol}//${window.location.host}/api/ws`)
  const sinceId = lastSeenEventId
  if (sinceId > 0) url.searchParams.set('since_id', String(sinceId))
  return url.toString()
}

function ensureSocket(): ReconnectingWS {
  if (_ws) return _ws
  // partysocket calls this on every (re)connect, so the latest
  // last-seen-id is sent each time. Without the function form, the
  // since_id frozen at first connect would force the server to replay
  // the same chunk of journal on every reconnect — and a long-lived
  // session would eventually exceed REPLAY_LIMIT and trigger
  // replay_truncated unnecessarily.
  const ws = new ReconnectingWS(buildWsUrl, [], {
    maxRetries: Infinity,
    minReconnectionDelay: 500,
    maxReconnectionDelay: 30_000,
    connectionTimeout: 10_000,
  })
  ws.addEventListener('open', () => {
    setWsConnected(true)
    // Unlike navigator.onLine, this proves the private AgentChat origin is
    // reachable again (including a Tailscale-only recovery). Flush every
    // durable phone prompt immediately instead of waiting for a manual retry.
    signalOutboxDeliveryOpportunity()
  })
  ws.addEventListener('close', () => setWsConnected(false))
  _ws = ws
  return ws
}

function resetWatchdog(ws: ReconnectingWS): void {
  if (_watchdogTimer !== null) window.clearTimeout(_watchdogTimer)
  _watchdogTimer = window.setTimeout(() => {
    _watchdogTimer = null
    // No traffic (not even a server ping) for WATCHDOG_TIMEOUT_MS — the
    // socket is almost certainly zombied (phone WebView resumed without
    // a close event, NAT mapping evicted, Wi-Fi handoff). Force a fresh
    // TCP connection so the since_id replay catches up whatever fired
    // while the link was silently dead.
    try {
      ws.reconnect(4000, 'ws-watchdog: no traffic for 35s')
    } catch {
      // partysocket can throw if it's mid-reconnect already; the next
      // open event will reset the watchdog so we're self-healing.
    }
  }, WATCHDOG_TIMEOUT_MS)
}

export function useWsBridge(enabled: boolean = true) {
  const qc = useQueryClient()
  useEffect(() => {
    if (!enabled) return
    const ws = ensureSocket()

    function patchMessages(
      chatId: string,
      topicId: string | null | undefined,
      patch: (msgs: Message[]) => Message[],
    ): void {
      qc.setQueryData<Message[]>(messagesKey(chatId, null), (prev) => {
        const list = prev ?? []
        return patch(list)
      })
      if (topicId) {
        qc.setQueryData<Message[]>(messagesKey(chatId, topicId), (prev) => {
          const list = prev ?? []
          return patch(list)
        })
      }
    }

    function patchMessageStatus(
      chatId: string,
      topicId: string | null | undefined,
      messageId: string,
      status: Message['status'],
      patch: Partial<Pick<Message, 'force_dispatch' | 'queue_position' | 'ts'>> = {},
    ): void {
      patchMessages(chatId, topicId, (msgs) =>
        msgs.map((m) => (
          m.id === messageId ? { ...m, status, queue_position: null, ...patch } : m
        )),
      )
    }

    function patchQueueEventMessages(
      chatId: string,
      topicId: string | null | undefined,
      messageIds: string[],
      patch: (message: Message) => Message,
    ): void {
      const ids = new Set(messageIds.filter(Boolean))
      if (ids.size === 0) return
      patchMessages(chatId, topicId, (msgs) =>
        msgs.map((message) => (ids.has(message.id) ? patch(message) : message)),
      )
    }

    function onMessage(ev: MessageEvent) {
      // Any byte from the server proves the link is alive — bump the
      // watchdog before we even try to parse, so a malformed frame
      // (vanishingly rare but possible) still counts as keepalive.
      resetWatchdog(ws)

      let parsed: JournalEvent
      try {
        parsed = JSON.parse(ev.data)
      } catch {
        return
      }
      // Server keepalive — pure ack, no journal payload.
      if (parsed.type === 'ping') return
      const k = parsed.kind
      const chatId = parsed.chat_id || undefined
      const msgId = parsed.message_id || undefined
      const payload = parsed.payload || {}
      const topicId = parsed.topic_id || (payload.topic_id as string | undefined) || undefined
      const eventId = Number(parsed.id ?? 0)
      const eventTs = Number(parsed.ts ?? 0)
      const finalTs = Number.isFinite(eventTs) && eventTs > 0
        ? Math.floor(eventTs)
        : Math.floor(Date.now() / 1000)

      if (eventId > 0) {
        if (eventId <= lastSeenEventId) return
        rememberEventId(eventId)
      }

      if (parsed.type === 'replay_truncated' || changesEngagedTopicState(k)) {
        qc.invalidateQueries({ queryKey: ['engaged-topics'] })
      }

      if (parsed.type === 'replay_truncated') {
        qc.invalidateQueries({ queryKey: ['chats'] })
        qc.invalidateQueries({ queryKey: ['lanes'] })
        refreshRetainedMessageReadModels(qc)
        return
      }

      if (k === 'pwa_updated') {
        prepareUpdate(String(payload.cache || parsed.id || 'latest'))
        return
      }

      // ── Streaming token-by-token path ────────────────────────────────
      // Idempotent across WS reconnect / journal replay: backend tags each
      // delta with `body_len_after` (absolute target length AFTER the delta
      // is applied). Replays of deltas the client already saw will have
      // body_len_after ≤ the local body length, and we drop them. Without
      // this guard a reconnect mid-stream visibly duplicated text on screen
      // ("the text keeps changing" bug). The final agent_reply event
      // replaces the body wholesale so the steady state is always correct,
      // but the dedup keeps the bubble honest while it's still streaming.
      if (k === 'agent_reply_delta' && chatId && msgId) {
        const delta = String(payload.delta || '')
        const bodyLenAfter = Number(payload.body_len_after ?? 0)
        if (!delta) return
        const updateLiveActivity = (): void => {
          qc.setQueryData<Chat[]>(['chats'], (cur) =>
            (cur ?? []).map((c) =>
              c.id === chatId
                ? { ...c, last_activity_at: finalTs, last_agent_reply_at: finalTs }
                : c,
            ),
          )
          qc.setQueryData<Chat>(['chat', chatId], (cur) =>
            cur ? { ...cur, last_activity_at: finalTs, last_agent_reply_at: finalTs } : cur,
          )
          if (topicId) {
            qc.setQueryData<Topic[]>(['topics', chatId], (cur) =>
              (cur ?? []).map((topic) =>
                topic.id === topicId
                  ? { ...topic, last_message_ts: finalTs, last_message_direction: 'outbound' }
                  : topic,
              ),
            )
          }
        }
        patchMessages(chatId, topicId, (msgs) => {
          const idx = msgs.findIndex((m) => m.id === msgId)
          if (idx >= 0) {
            const next = msgs.slice()
            const prev = next[idx]!
            const currentBody = prev.body || ''
            // Replay guard: if backend told us the absolute len AFTER this
            // delta and our body is already at least that long, this is a
            // duplicate event. Drop it; the live stream will catch up.
            if (bodyLenAfter > 0 && bodyLenAfter <= currentBody.length) {
              return next
            }
            next[idx] = { ...prev, body: currentBody + delta, ts: finalTs }
            return next
          }
          // First delta — insert a fresh outbound bubble so the user sees
          // the agent "start typing" within tens of ms.
          const placeholder: Message = {
            id: msgId,
            topic_id: topicId ?? null,
            external_message_id: null,
            direction: 'outbound',
            user: 'agent',
            ts: finalTs,
            body: delta,
            status: 'dispatched',
            queue_position: null,
            force_dispatch: false,
            reply_to_message_id: null,
            unread: false,
            deleted: false,
          }
          return [...msgs, placeholder]
        })
        updateLiveActivity()
        return
      }

      // ── Final agent_reply (also covers reset_chat welcome) ───────────
      if (k === 'agent_reply' && chatId && msgId) {
        const body = String(payload.body || '')
        const direction: Message['direction'] = payload.direction === 'system' ? 'system' : 'outbound'
        const user = direction === 'system' ? 'system' : 'agent'
        const chatReplyPatch = direction === 'system' ? {} : { last_agent_reply_at: finalTs }
        // Round 9: agent_reply payloads from the topic_dispatch lib carry
        // verdict + actions. Cache-only render (lost on cold reload until
        // a follow-up migration persists them on the messages table).
        const verdict = (payload.verdict as string | null | undefined) ?? null
        const actionsRaw = payload.actions as Message['actions'] | undefined
        const actions = Array.isArray(actionsRaw) ? actionsRaw : undefined
        const attachmentsRaw = payload.attachments as Message['attachments'] | undefined
        const attachments = Array.isArray(attachmentsRaw) ? attachmentsRaw : undefined
        const failedSourceMessageId = payload.delivery_error === true &&
          typeof payload.source_message_id === 'string'
          ? payload.source_message_id
          : null
        patchMessages(chatId, topicId, (msgs) => {
          const idx = msgs.findIndex((m) => m.id === msgId)
          if (idx >= 0) {
            const next = msgs.slice()
            next[idx] = {
              ...next[idx]!, body, direction, user, status: 'received',
              ts: finalTs,
              verdict, actions,
              attachments: attachments ?? next[idx]!.attachments,
            }
            return next
          }
          // Wasn't already streamed — insert as new bubble.
          const placeholder: Message = {
            id: msgId,
            topic_id: topicId ?? null,
            external_message_id: null,
            direction,
            user,
            ts: finalTs,
            body,
            status: 'received',
            queue_position: null,
            force_dispatch: false,
            reply_to_message_id: null,
            unread: false,
            deleted: false,
            verdict,
            actions,
            attachments,
          }
          return [...msgs, placeholder]
        })
        if (failedSourceMessageId) {
          patchMessageStatus(chatId, topicId, failedSourceMessageId, 'error')
        }
        // Tool-call progress for this msgId is now stale — clear it so
        // the rolling indicator doesn't linger after the bubble settles.
        qc.setQueryData(['tool_progress', chatId, msgId], null)
        // Source-of-truth tail refresh for final message metadata. The event
        // patches the visible reply inline; this warm only reconciles durable
        // fields (source inbound status, attachments, verdict/actions) without
        // replacing the mounted full transcript.
        preloadMessageReadModel(qc, chatId, topicId ?? null)
        window.setTimeout(() => preloadMessageReadModel(qc, chatId, topicId ?? null), 750)
        // Optimistic badge bump: increment chat.unread in-memory
        // immediately so the chat-list badge updates within a frame,
        // not after a 100-300ms /api/chats round-trip. The
        // invalidateQueries below reconciles against truth a moment
        // later — but the user sees the new count right away.
        // Skipped when the user is currently viewing the chat (the
        // auto-mark-read below will keep it at 0 anyway, AND we don't
        // want to flash +1 in their face).
        const isViewingChat = (typeof window !== 'undefined' && chatId &&
          window.location.pathname === `/chats/${chatId}`)
        if (chatId && !isViewingChat) {
          qc.setQueryData<Chat[]>(['chats'], (cur) =>
            (cur ?? []).map((c) =>
              c.id === chatId
                ? { ...c, last_activity_at: finalTs, ...chatReplyPatch, unread: (c.unread ?? 0) + 1 }
                : c,
            ),
          )
        } else if (chatId) {
          qc.setQueryData<Chat[]>(['chats'], (cur) =>
            (cur ?? []).map((c) =>
              c.id === chatId
                ? { ...c, last_activity_at: finalTs, ...chatReplyPatch }
                : c,
            ),
          )
        }
        if (chatId) {
          preloadMessageReadModel(qc, chatId, topicId ?? null)
          qc.setQueryData<Chat>(['chat', chatId], (cur) =>
            cur ? { ...cur, last_activity_at: finalTs, ...chatReplyPatch } : cur,
          )
          if (topicId) {
            qc.setQueryData<Topic[]>(['topics', chatId], (cur) =>
              (cur ?? []).map((topic) =>
                topic.id === topicId
                  ? { ...topic, last_message_ts: finalTs, last_message_direction: direction, status: 'idle' }
                  : topic,
              ),
            )
          }
        }
        qc.invalidateQueries({ queryKey: ['chats'] })
        qc.invalidateQueries({ queryKey: ['lanes'] })
        // Open chat-detail header reads lane_status / lane_last_heartbeat
        // off the chats×lanes join. Without this line the header stays
        // on whatever it last fetched (could be 'starting' or 'crashed'
        // from a recent reset) and never flips back to idle/working —
        // exactly what made Codex #3 read "Offline" while replying.
        if (chatId) qc.invalidateQueries({ queryKey: ['chat', chatId] })
        // Per-topic status is derived from message statuses on each
        // topic; refetch so the header dot flips back from blue to
        // green the moment the topic's last message hits 'replied'.
        if (chatId) qc.invalidateQueries({ queryKey: ['topics', chatId] })
        if (direction !== 'system') {
          window.dispatchEvent(new CustomEvent('agentchat:agent-reply-final', {
            detail: {
              chatId,
              topicId: topicId ?? null,
              messageId: msgId,
              body,
              ts: finalTs,
            },
          }))
        }
        return
      }

      // ── Tool-call progress (Round 9 — topic_dispatch lib) ────────────
      // Long autonomous turns (web search, ripgrep, multi-min Bash) are
      // silent on the visible reply stream — the model is busy but
      // produces no text deltas. The lib emits one rolling line per
      // tool_use event; we stash it in a TanStack cache slot keyed by
      // msgId so the streaming bubble can render a "🔧 ..." indicator
      // until the final agent_reply lands.
      if (k === 'tool_call_progress' && chatId && msgId) {
        const line = String(payload.line || '')
        if (!line) return
        qc.setQueryData(
          ['tool_progress', chatId, msgId],
          { message_id: msgId, line, ts: Math.floor(Date.now() / 1000) },
        )
        return
      }

      // ── Reset chat → invalidate so the welcome message shows up ──────
      if (k === 'reset_chat' && chatId) {
        refreshMessageReadModel(qc, chatId, topicId ?? null)
        qc.invalidateQueries({ queryKey: ['chats'] })
        qc.invalidateQueries({ queryKey: ['chat', chatId] })
        return
      }

      // ── Cross-device read sync ───────────────────────────────────────
      // Mark opened the chat/topic on another device. Mirror the read
      // receipt here and tell the service worker to drop matching OS
      // notifications without waiting for the next refetch tick.
      if (k === 'chat_read' && chatId) {
        const readTopicId = topicId ?? null
        const markedRead = Number(payload.marked_read ?? 0)
        const readAllTopics = payload.all_topics === true
        const cachedChat = (
          qc.getQueryData<Chat>(['chat', chatId])
          ?? qc.getQueryData<Chat[]>(['chats'])?.find((chat) => chat.id === chatId)
        )
        const versions = typeof payload.message_scope_versions === 'object' && payload.message_scope_versions !== null
          ? payload.message_scope_versions as { chat?: number | null; topic?: number | null }
          : undefined
        applyReadReceiptToClient(
          qc,
          chatId,
          {
            topicId: readTopicId,
            allTopics: readAllTopics,
            markedRead: Number.isFinite(markedRead) ? markedRead : undefined,
          },
          versions,
        )
        const topicBackedChat = cachedChat?.type === 'auto-spawn'
        const shouldClearNotifications = readAllTopics || Boolean(readTopicId) || !topicBackedChat
        if (shouldClearNotifications) clearReadNotificationsForChat(chatId, readTopicId)
        return
      }

      // ── Generic kinds → cache invalidation ───────────────────────────
      // Helper: any event that changes lane state must invalidate the
      // open chat detail too — its header pulls lane_status off the
      // chats×lanes join. Stale chat detail = stuck "Offline" / "Working"
      // pill that doesn't match reality.
      if (k === 'message_updated' && chatId) {
        refreshMessageReadModel(qc, chatId, topicId ?? null)
        return
      }
      if (k === 'enqueued' || k === 'force_now' || k === 'dispatched' || k === 'idle' ||
          k === 'busy'    || k === 'replied') {
        if (chatId && msgId) {
          if (k === 'enqueued') {
            const queuePosition = Number(payload.queue_position ?? payload.new_position)
            const enqueuedPatch: Partial<Pick<Message, 'force_dispatch' | 'queue_position'>> = {
              force_dispatch: payload.force === true,
            }
            if (Number.isFinite(queuePosition)) enqueuedPatch.queue_position = queuePosition
            patchMessageStatus(
              chatId,
              topicId,
              msgId,
              'queued',
              enqueuedPatch,
            )
            preloadMessageReadModel(qc, chatId, topicId ?? null)
          } else if (k === 'force_now') {
            const steered = payload.steered === true
            patchMessageStatus(
              chatId,
              topicId,
              msgId,
              steered ? 'dispatched' : 'queued',
              { queue_position: steered ? null : 0, force_dispatch: true },
            )
          } else if (k === 'dispatched') {
            const mergedIds = Array.isArray(payload.merged_message_ids)
              ? payload.merged_message_ids.filter((id): id is string => typeof id === 'string')
              : []
            // Preserve each message's created/display ts. Rewriting to the
            // journal event ts coalesced Agent Mail batches to one HH:mm on
            // desktop while phone refetches could still differ.
            patchQueueEventMessages(chatId, topicId, [msgId, ...mergedIds], (message) => ({
              ...message,
              status: 'dispatched',
              queue_position: null,
            }))
          } else if (k === 'replied') {
            patchMessageStatus(chatId, topicId, msgId, 'replied')
          }
        }
        qc.invalidateQueries({ queryKey: ['chats'] })
        qc.invalidateQueries({ queryKey: ['lanes'] })
        if (chatId) qc.invalidateQueries({ queryKey: ['chat', chatId] })
        // Per-topic status is derived from message statuses; refetch
        // topics on every queue-state transition so each topic's badge
        // (idle/queued/working/error) tracks reality in real time.
        if (chatId) qc.invalidateQueries({ queryKey: ['topics', chatId] })
      } else if (k === 'turn_active' || k === 'turn_stopped' || k === 'turn_ended') {
        // `dispatched` fires ~80 lines BEFORE lane_worker writes active_turns,
        // so the refetch it triggers reads the table too early and the Stop
        // button never appears. These three are journalled after the row is
        // written/cleared, which is why the button is gated on them.
        if (chatId) qc.invalidateQueries({ queryKey: ['topics', chatId] })
        if (chatId) qc.invalidateQueries({ queryKey: ['chat', chatId] })
        qc.invalidateQueries({ queryKey: ['chats'] })
      } else if (k === 'topic_spawned') {
        qc.invalidateQueries({ queryKey: ['chats'] })
        if (chatId) {
          scheduleTopicsInvalidate(qc, chatId)
        }
      } else if (k === 'topic_closed' || k === 'topic_reopened' ||
                 k === 'topic_renamed' || k === 'topic_model_changed' ||
                 k === 'topic_created') {
        // Round 3: per-topic state changes update the GroupListView.
        if (chatId) {
          scheduleTopicsInvalidate(qc, chatId)
        }
        qc.invalidateQueries({ queryKey: ['chats'] })
      } else if (
        k === 'chat_model_changed' ||
        k === 'chat_session_title_changed' ||
        k === 'chat_snoozed' ||
        k === 'chat_unsnoozed'
      ) {
        qc.invalidateQueries({ queryKey: ['chats'] })
        if (chatId) qc.invalidateQueries({ queryKey: ['chat', chatId] })
      } else if (k === 'lane_missing' || k === 'dispatch_error' ||
                 k === 'watchdog_stale_lane') {
        if (k === 'dispatch_error' && chatId) {
          const failedMessageId = typeof payload.message_id === 'string'
            ? payload.message_id
            : typeof payload.source_message_id === 'string'
              ? payload.source_message_id
              : null
          if (failedMessageId) patchMessageStatus(chatId, topicId, failedMessageId, 'error')
          preloadMessageReadModel(qc, chatId, topicId ?? null)
          qc.invalidateQueries({ queryKey: ['topics', chatId] })
        }
        qc.invalidateQueries({ queryKey: ['lanes'] })
        qc.invalidateQueries({ queryKey: ['chats'] })
        if (chatId) qc.invalidateQueries({ queryKey: ['chat', chatId] })
      } else if (k === 'snooze' || k === 'unsnooze' || k === 'pin' || k === 'unpin') {
        if (chatId) {
          refreshMessageReadModel(qc, chatId, topicId ?? null)
          if (k === 'snooze' || k === 'unsnooze') scheduleTopicsInvalidate(qc, chatId)
        }
        qc.invalidateQueries({ queryKey: ['chats'] })
      } else if (k === 'usage_limited' || k === 'usage_recovered') {
        qc.invalidateQueries({ queryKey: ['lanes'] })
        qc.invalidateQueries({ queryKey: ['chats'] })
        qc.invalidateQueries({ queryKey: ['usage_states'] })
        if (chatId) qc.invalidateQueries({ queryKey: ['chat', chatId] })
      } else if (k === 'codex_account_changed' || k === 'codex_quota_failover') {
        // Mode toggle, tracker restriction, or an in-turn failover: the
        // header chip and Settings read model must follow immediately.
        qc.invalidateQueries({ queryKey: ['codex-availability'] })
        qc.invalidateQueries({ queryKey: ['chats'] })
        if (chatId) qc.invalidateQueries({ queryKey: ['chat', chatId] })
      }
    }

    function onOpen(): void {
      // Fresh connection — start the watchdog clock. From this point on
      // we expect a server ping every PING_INTERVAL_SECONDS and any
      // event resets the timer.
      resetWatchdog(ws)
      // A cold window can inherit a persisted cursor newer than its cache.
      // Reconcile its mounted scopes even when there is nothing to replay.
      refreshRetainedMessageReadModels(qc)
    }

    ws.addEventListener('message', onMessage)
    ws.addEventListener('open', onOpen)
    // The socket may already be OPEN by the time this effect runs (the
    // module-level singleton is reused across remounts). Kick the
    // watchdog so we don't sit cold waiting for the first ping.
    if (ws.readyState === ReconnectingWS.OPEN) onOpen()
    return () => {
      ws.removeEventListener('message', onMessage)
      ws.removeEventListener('open', onOpen)
    }
  }, [qc, enabled])
}
